import {useTranslation} from 'react-i18next';
import {Check, Hourglass, X} from 'lucide-react';
import {OutcomeBadge, ProgressRing, cn} from '@chekinapp/ui';
import {STATUSES} from '@chekin/core';
import {MAX_ESTIMATE, useEstimatedProgress} from './useEstimatedProgress';
import {useRotatingMessage} from './useRotatingMessage';

export type CaptureCheck = 'face' | 'lines' | 'sharp' | 'compare';

const CHECK_MESSAGE_KEY: Record<CaptureCheck, string> = {
  face: 'checking_msg_face',
  lines: 'checking_msg_lines',
  sharp: 'checking_msg_sharp',
  compare: 'checking_msg_compare',
};

const CHECK_CHIP_KEY: Partial<Record<CaptureCheck, string>> = {
  face: 'check_chip_face',
  lines: 'check_chip_lines',
  compare: 'check_chip_compare',
};

const PATIENCE_MESSAGE_KEYS = [
  'checking_msg_still',
  'checking_msg_extra',
  'checking_msg_almost',
];

const RING_SIZE = 176;

type CheckChipStatus = 'done' | 'failed' | 'running' | 'waiting';

function CheckChip({label, status}: {label: string; status: CheckChipStatus}) {
  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex items-center gap-2 text-[14px] font-medium leading-5 transition-colors duration-200',
        status === 'done'
          ? 'text-[var(--chekin-green-800)]'
          : status === 'failed'
          ? 'text-[var(--chekin-danger)]'
          : 'text-[var(--chekin-text-secondary)]',
      )}
    >
      {status === 'done' && (
        <span
          aria-hidden
          className="grid size-[18px] flex-none place-items-center rounded-full bg-[var(--chekin-success)] text-white motion-safe:animate-[capture-pop_0.45s_cubic-bezier(0.22,0.8,0.2,1)_both]"
        >
          <Check className="size-3" strokeWidth={3.2} />
        </span>
      )}
      {status === 'failed' && (
        <span
          aria-hidden
          className="grid size-[18px] flex-none place-items-center rounded-full bg-[var(--chekin-danger)] text-white motion-safe:animate-[capture-pop_0.45s_cubic-bezier(0.22,0.8,0.2,1)_both]"
        >
          <X className="size-3" strokeWidth={3.2} />
        </span>
      )}
      {status === 'running' && (
        <span
          aria-hidden
          className="size-[18px] flex-none rounded-full border-[2.5px] border-[var(--capture-spinner-track)] border-t-[var(--capture-spinner-indicator)] motion-safe:animate-[capture-spin_0.8s_linear_infinite]"
        />
      )}
      {status === 'waiting' && (
        <span
          aria-hidden
          className="size-[18px] flex-none rounded-full border-[2.5px] border-[var(--capture-spinner-track)]"
        />
      )}
      {label}
    </span>
  );
}

export type CaptureCheckingViewProps = {
  state: STATUSES;
  checks: CaptureCheck[];
  passedChecks?: CaptureCheck[];
  isFinalStep?: boolean;
  isServiceFailure?: boolean;
  className?: string;
};

export function CaptureCheckingView({
  state,
  checks,
  passedChecks = [],
  isFinalStep,
  isServiceFailure,
  className,
}: CaptureCheckingViewProps) {
  const {t} = useTranslation('identity-verification');
  const progress = useEstimatedProgress(state);
  const isLoading = state === STATUSES.loading;
  const isSuccess = state === STATUSES.success;
  const isTakingLong = isLoading && progress >= MAX_ESTIMATE;
  const currentCheck = checks.find(check => !passedChecks.includes(check));
  const messageKeys = [
    ...(currentCheck ? [CHECK_MESSAGE_KEY[currentCheck]] : []),
    ...PATIENCE_MESSAGE_KEYS,
  ];
  const {message, index} = useRotatingMessage(messageKeys, {
    loopFrom: currentCheck ? 1 : 0,
    paused: !isLoading,
    resetKey: currentCheck,
  });
  const isRingSettled = !isLoading && progress >= 100;
  const showsDone = isSuccess && isFinalStep && isRingSettled;
  const chipChecks = checks.filter(check => CHECK_CHIP_KEY[check]);
  const failedCheck =
    state === STATUSES.error && isRingSettled && !isServiceFailure
      ? chipChecks.find(check => !passedChecks.includes(check))
      : undefined;
  const runningCheck = isRingSettled
    ? undefined
    : chipChecks.find(check => !passedChecks.includes(check));
  const getChipStatus = (check: CaptureCheck): CheckChipStatus => {
    if (passedChecks.includes(check) || (isSuccess && isRingSettled)) return 'done';
    if (check === failedCheck) return 'failed';
    return check === runningCheck ? 'running' : 'waiting';
  };

  const statusMessage = isLoading
    ? t(message)
    : isRingSettled
    ? t(
        isSuccess
          ? 'everything_looks_good'
          : isServiceFailure
          ? 'check_could_not_finish'
          : 'found_problem_with_photo',
      )
    : t('checking_msg_finishing');

  return (
    <div
      className={cn(
        'iv-capture-checking flex w-full flex-1 flex-col items-center gap-[22px] py-4 before:flex-1 before:content-[""] after:flex-[2] after:content-[""]',
        className,
      )}
    >
      <div className="grid place-items-center" style={{height: RING_SIZE}}>
        {showsDone ? (
          <OutcomeBadge tone="success" className="[--outcome-badge-size:84px]">
            <Check strokeWidth={3} />
          </OutcomeBadge>
        ) : (
          <ProgressRing
            value={progress}
            size={RING_SIZE}
            strokeWidth={10}
            halo
            transitionMs={120}
            tone={isRingSettled ? (isSuccess ? 'success' : 'danger') : 'brand'}
            labelClassName="text-[34px] font-bold tabular-nums [color:var(--chekin-text-primary)]"
            label={
              isRingSettled ? (
                isSuccess ? (
                  <Check className="size-16 text-[var(--chekin-success)] motion-safe:animate-[capture-pop_0.45s_cubic-bezier(0.22,0.8,0.2,1)_both]" />
                ) : (
                  <X className="size-16 text-[var(--chekin-danger-soft)] motion-safe:animate-[capture-pop_0.45s_cubic-bezier(0.22,0.8,0.2,1)_both]" />
                )
              ) : undefined
            }
          />
        )}
      </div>
      {showsDone ? (
        <p
          role="status"
          aria-live="polite"
          className="m-0 flex items-center gap-2 text-[14px] font-medium leading-5 text-[var(--chekin-text-primary)]"
        >
          <span
            aria-hidden
            className="size-4 flex-none rounded-full border-2 border-[var(--capture-spinner-track)] border-t-[var(--capture-spinner-indicator)] motion-safe:animate-[capture-spin_0.8s_linear_infinite]"
          />
          {t('taking_you_to_details')}
        </p>
      ) : (
        <p
          key={isLoading ? index : state}
          role="status"
          aria-live="polite"
          className="m-0 min-h-[22px] text-center text-[15px] font-medium leading-[22px] text-[var(--chekin-text-primary)] motion-safe:animate-[capture-fade_0.4s_ease-out]"
        >
          {statusMessage}
        </p>
      )}
      {!showsDone && chipChecks.length > 0 && (
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          {chipChecks.map(check => (
            <CheckChip
              key={check}
              label={t(CHECK_CHIP_KEY[check] ?? '')}
              status={getChipStatus(check)}
            />
          ))}
        </div>
      )}
      {isTakingLong && (
        <p className="m-0 flex max-w-[420px] items-start gap-2.5 rounded-[12px] bg-[var(--chekin-neutral-100)] px-4 py-3 text-[14px] leading-5 text-[var(--chekin-text-secondary)] motion-safe:animate-[capture-fade_0.4s_ease-out]">
          <Hourglass
            aria-hidden
            className="mt-0.5 size-4 flex-none text-[var(--chekin-color-brand-blue)]"
          />
          {t('checking_slow_notice')}
        </p>
      )}
    </div>
  );
}
